import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { AppCtxComponent } from 'js/reactAppCtx';
import { DesAccConstants } from '../constant/desAccConstants';
import { setLocale } from 'js/localeService';
import { InteropManager} from 'xcs-base-acc-client/src/descommon/src/js/interopUtils';
import { UtilityManager } from './baseUtils';



export class DesBaseCatalogAcc extends HTMLElement {

    // Flag to indicate if the initial locale has been processed and rendered
    _hasInitialLocaleBeenRendered = false;
    _interopManager = null;
    _utilityManager = null;

    constructor(ViewModel) {
        super();
        this.ViewModel = ViewModel;
        this.debounceTimer = 100;
        this._interopManager = new InteropManager(this, {
            onLocaleUpdate: (element, payload) => {
                ReactDOM.unmountComponentAtNode(element);
                element.setAttribute(DesAccConstants.LOCALE, payload.value);
            }
        });
        this._utilityManager = new UtilityManager(this);
    }

    connectedCallback() {

        // Reset flag on connection to ensure correct behavior if component is re-connected
        this._hasInitialLocaleBeenRendered = false;

        // Subscribe to interop events using the manager
        this._interopManager.subscribe();

        this.applyDefaultStyling();

        this._utilityManager.manageLocale(this.locale);
    }

    /**
     * Use for defaultStyling of ACC
     */
    applyDefaultStyling() {
        this.style.height = '100%';
    }

    getProps() {
        return {
        };
    }

    /**
     * Helper function
     */
    createReactElement() {
        return React.createElement(
            this.ViewModel,
            {
                accsessionid: this.accsessionid,
                collaborationspaceid: this.collaborationspaceid,
                locale: this.locale,
                ...this.getProps()
            },
            React.createElement('slot')
        );
    }

    /**
     * Get acc session id.
     *
     * @readonly
     * @memberof DesBaseCatalogAcc
     * @return {string} acc-session-id
     */
    get accsessionid() {
        return this.getAttribute(DesAccConstants.ACC_SESSION_ID);
    }

    /**
     * Get collaborationspaceid
     *
     * @readonly
     * @memberof DesBaseCatalogAcc
     * @return {string} collaborationspaceid
     */
    get collaborationspaceid() {
        return this.getAttribute(DesAccConstants.COLLABORATION_SPACE_ID);
    }

    /**
     * Get locale
     *
     * @readonly
     * @memberof DesBaseCatalogAcc
     * @return {string} locale
     */
    get locale() {
        return this.getAttribute(DesAccConstants.LOCALE);
    }

    /**
     * Get provider
     *
     * @readonly
     * @memberof DesBaseCatalogAcc
     */
    get provider() {
        return this.getAttribute(DesAccConstants.PROVIDER);
    }

    /**
     * Get catalog space id
     *
     * @readonly
     * @memberof DesBaseCatalogAcc
     * @return {string} catalog space id.
     */
    get catalogspaceId() {
        return this.getAttribute(DesAccConstants.CATALOG_SPACE_ID);
    }

    /**
     * Get limit
     *
     * @readonly
     * @memberof DesBaseCatalogAcc
     * @return {string} limit
     */
    get limit() {
        return this.getAttribute(DesAccConstants.LIMIT);
    }

    /**
     * Get display mode value
     *
     * @readonly
     * @memberof DesBaseCatalogAcc
     * @return {string} display mode value
     */
    get display() {
        return DesBaseCatalogAcc.getDisplayMode(this.getAttribute(DesAccConstants.DISPLAY));
    }

    /**
     * Get hide-element value
     *
     * @readonly
     * @memberof DesBaseCatalogAcc
     * @return {string} Comma-separated values
     */
    get hideElements() {
        return this.getAttribute(DesAccConstants.HIDE_ELEMENTS);
    }

    /**
     * Observ acc attributes.
     *
     * @readonly
     * @static
     * @memberof DesBaseCatalogAcc
     */
    static get observedAttributes() {
        return [
            DesAccConstants.ACC_SESSION_ID,
            DesAccConstants.CATALOG_SPACE_ID,
            DesAccConstants.COLLABORATION_SPACE_ID,
            DesAccConstants.LOCALE,
            DesAccConstants.PROVIDER,
            DesAccConstants.HIDE_ELEMENTS,
            DesAccConstants.DISPLAY
        ];
    }

    attributeChangedCallback(name, oldValue, newValue) {

        // This ensures the initial render function (`performInitialRender`) handles the very first render,
        // and `attributeChangedCallback` doesn't trigger a duplicate.
        if (name === DesAccConstants.LOCALE && !this._hasInitialLocaleBeenRendered) {
            return; // Suppress this initial attribute change from triggering a re-render
        }
        if (oldValue !== newValue) {
            this.debounceCreateElement();
        }
    }

    debounceCreateElement() {
        const _this = this;
        if (_this.debounceFuncRef) {
            clearTimeout(_this.debounceFuncRef);
        }

        _this.debounceFuncRef = setTimeout(() => {
            if (_this.locale && _this.locale.length > 0) {
                setLocale(_this.locale);
            }
            ReactDOM.render(<AppCtxComponent>{_this.createReactElement()}</AppCtxComponent>, _this);
        }, this.debounceTimer);
    }

    disconnectedCallback() {
        ReactDOM.unmountComponentAtNode(this);
        this._interopManager.unsubscribe();
    }

    static getDisplayMode(display) {
        const displayList = [DesAccConstants.REGULAR, DesAccConstants.COMPACT, DesAccConstants.CUSTOM, DesAccConstants.LIST];
        // If given value does not match with regular, compact and custom then set default value
        if (!displayList.includes(display)) {
            display = displayList[0];
        }

        return display;
    }

    static commonObservedAttr() {
        return [
            DesAccConstants.ACC_SESSION_ID,
            DesAccConstants.locale, DesAccConstants.PROVIDER
        ];
    }
}
